import api from '@/lib/axiosInstance'
import Helper from '@/lib/helper'
import { warnIfLow } from './lowBalance'

/**
 * Typed client for the agent-facing AI endpoints (`/ai/*`).
 *
 * Every success body is `{ status: true, ... }`; failures are HTTP 4xx/5xx
 * with `{ status: false, error, code, details? }`. The shared axios instance
 * defaults to multipart, so JSON bodies pass an explicit content type.
 */

const JSON_HEADERS = { headers: { 'Content-Type': 'application/json' } }

export type AIErrorCode =
    | 'AI_INSUFFICIENT_TOKENS'
    | 'AI_NOT_CONFIGURED'
    | 'AI_DISABLED'
    | 'AI_LIMIT_REACHED'
    | 'AI_INVALID_INPUT'
    | 'AI_PROVIDER_ERROR'
    | 'AI_RATE_LIMITED'
    | 'AI_NOT_FOUND'
    | string

export interface AIErrorBody {
    status: false
    error: string
    code?: AIErrorCode
    details?: {
        required?: number
        available?: number
        /** On AI_INSUFFICIENT_TOKENS: what the run needs and what is left, in tokens. */
        requiredTokens?: number
        availableTokens?: number
        [key: string]: unknown
    }
}

export interface AIBalance {
    /** The balance after the last top-up or renewal, in ringgit billing. */
    referenceTokens?: number
    availableTokens: number
    bonusTokens: number
    lifetimeTokensPurchased: number
    lifetimeBonusTokens: number
    lifetimeTokensUsed: number
}

export interface AIOperation {
    operationKey: string
    name: string
    description: string
    tokenCost: number
    audience: string
}

export interface AIStatus {
    enabled: boolean
    openai: { configured: boolean; message?: string }
    features: Record<string, boolean>
}

export interface AIEnhanceAction {
    key: EnhanceActionKey
    label: string
    operationKey: string
}

export type SocialPlatform = 'facebook' | 'instagram' | 'threads' | 'linkedin'

export interface AIOverview {
    balance: AIBalance | null
    operations: AIOperation[]
    status: AIStatus
    enhanceActions: AIEnhanceAction[]
    socialPlatforms: SocialPlatform[]
    purchases: { enabled: boolean; webhookConfigured: boolean }
    subscription: AISubscription | null
    /** In ringgit billing; `unlimited` for whitelisted accounts. */
    credits?: { unlimited: boolean; available?: string } | null
}

export interface AIPagination {
    currentPage: number
    pageSize: number
    totalCount: number
    totalPages: number
    hasNextPage: boolean
    hasPreviousPage: boolean
}

export type AIUsageStatus = 'pending' | 'completed' | 'failed' | 'refunded'

export interface AIUsageRow {
    id: number
    requestId: string
    operationKey: string
    operationName: string
    tokenCost: number
    status: AIUsageStatus
    referenceType: string | null
    referenceId: number | null
    latencyMs: number | null
    errorCode: string | null
    createdAt: string
    completedAt: string | null
}

export type AITransactionType = 'PURCHASE' | 'BONUS' | 'USAGE' | 'REFUND' | 'ADMIN_ADJUSTMENT' | 'EXPIRATION' | 'REVERSAL'

export interface AITransactionRow {
    id: number
    type: AITransactionType
    amount: number
    balanceBefore: number
    balanceAfter: number
    operationKey: string | null
    reason: string | null
    createdAt: string
}

export interface AIGenerationRow {
    id: number
    operationKey: string
    action: string | null
    listingId: number | null
    output: unknown
    accepted: boolean
    createdAt: string
}

export interface AIUsageReceipt {
    requestId: string
    operationKey: string
    tokenCost: number
    balanceAfter: number
    /** In ringgit billing: tokens the run used, left after it, and the balance after the last top-up or renewal. */
    credits?: { unlimited: boolean; charged?: string; balance?: string; tokensCharged?: number; tokensLeft?: number; tokensReference?: number }
}

/** After an AI run: warn when the balance is nearly gone. */
const afterRun = <T extends { usage?: AIUsageReceipt }>(data: T): T => {
    const credits = data?.usage?.credits
    if (credits && !credits.unlimited) warnIfLow(credits.tokensLeft, credits.tokensReference)
    return data
}

/**
 * The same warning for runs whose answer carries no receipt (an intake job,
 * a transcription): uses the receipt when there is one, otherwise asks for
 * the balance. Never throws.
 */
export const warnIfLowAfterRun = (usage?: AIUsageReceipt | null): void => {
    const credits = usage?.credits
    if (credits && typeof credits.tokensLeft === 'number') {
        if (!credits.unlimited) warnIfLow(credits.tokensLeft, credits.tokensReference)
        return
    }
    getAIOverview()
        .then((overview) => {
            const balance = overview?.balance
            if (balance && !overview?.credits?.unlimited) warnIfLow(balance.availableTokens, balance.referenceTokens)
        })
        .catch(() => undefined)
}

export type ListingTone = 'professional' | 'warm' | 'luxury' | 'concise' | 'persuasive'
export type ListingLanguage = 'en' | 'ms' | 'zh'

export interface ListingDraft {
    propertyType?: string | number | null
    propertySubType?: string | number | null
    purpose?: 'sale' | 'rent' | null
    price?: number | string | null
    township?: string | null
    city?: string | number | null
    state?: string | number | null
    postcode?: string | null
    bedrooms?: number | null
    bathrooms?: number | null
    builtUp?: string | number | null
    landArea?: string | number | null
    tenure?: string | number | null
    furnishing?: string | number | null
    yearOfCompletion?: string | number | null
    facilities?: string[]
    headline?: string | null
    description?: string | null
    [key: string]: unknown
}

export interface ListingNotes {
    notes?: string
    sellingPoints?: string[]
    nearbyLandmarks?: string[]
    targetAudience?: string
    tone?: ListingTone
    language?: ListingLanguage
}

export interface ListingCopy {
    title: string
    shortDescription: string
    fullDescription: string
    highlights: string[]
    sellingPoints: string[]
    locationDescription: string
    tags: string[]
    seoTitle: string
    seoDescription: string
    socialCopy: string
    factsUsed: string[]
    missingFacts: string[]
    disclaimer: string
}

export interface GenerateListingResponse {
    listingId: number | null
    facts: Record<string, unknown>
    copy: ListingCopy
    missingFacts: string[]
    generationId: number | null
    usage: AIUsageReceipt
}

export type EnhanceActionKey = 'rewrite' | 'professional' | 'shorter' | 'persuasive' | 'translate' | 'headline' | 'seo' | 'whatsapp'

export interface EnhanceListingResponse {
    listingId: number
    action: EnhanceActionKey
    actionLabel: string
    result: string
    notes: string[]
    factsUsed: string[]
    missingFacts: string[]
    generationId: number | null
    usage: AIUsageReceipt
}

export interface SocialPost {
    platform: SocialPlatform
    text: string
    hashtags: string[]
    callToAction: string
}

export interface SocialResponse {
    posts: SocialPost[]
    factsUsed: string[]
    missingFacts: string[]
    generationId: number | null
    usage: AIUsageReceipt
    publishing: { available: boolean; message: string }
}

export type VideoAspectRatio = '9:16' | '16:9' | '1:1'

export interface VideoScene {
    order: number
    label: string
    durationSeconds: number
    visual: string
    voiceover: string
    onScreenText: string
}

export interface VideoBrief {
    title: string
    durationSeconds: number
    aspectRatio: VideoAspectRatio
    scenes: VideoScene[]
    providerPrompt: string
    callToAction: string
    factsUsed: string[]
    missingFacts: string[]
}

export interface VideoBriefResponse {
    brief: VideoBrief
    generationId: number | null
    usage: AIUsageReceipt
    generation: { available: boolean; message: string }
}

export interface AIPackage {
    id: number
    name: string
    description: string | null
    tokenAmount: number
    bonusTokens: number
    totalTokens: number
    /** What is charged today; `listPrice` is the usual price while a promotion runs. */
    price: number | string
    listPrice: number | string
    promotion: { name: string; bonusTokens: number; discountPercent: number; endsAt: string | null } | null
    currency: string
    validityDays: number | null
}

export type BillingInterval = 'month' | 'year'

/** A plan agents subscribe to: `tokenAmount` is credited every billing period. */
export interface AIPlan {
    id: number
    name: string
    description: string | null
    tagline: string | null
    features: string[]
    tokenAmount: number
    price: number
    currency: string
    billingInterval: BillingInterval
    /** False until Stripe holds a recurring price for it. */
    available: boolean
    /** What choosing this plan would do for the agent now, as the API will charge it. */
    change?: AIPlanChange
}

/**
 * The API's quote for moving to a plan: 'upgrade' is charged now (less the
 * unused share of the current plan) and starts a new period; 'downgrade'
 * starts at `effectiveFrom` with nothing charged; 'scheduled' is the plan a
 * downgrade already chosen starts at `effectiveFrom`; 'blocked' says why not.
 */
export interface AIPlanChange {
    kind: 'new' | 'current' | 'upgrade' | 'downgrade' | 'scheduled' | 'blocked'
    chargeNow?: number
    discount?: number
    unusedTokens?: number
    startsNewPeriod?: boolean
    effectiveFrom?: string | null
    message?: string
}

export type AISubscriptionStatus = 'active' | 'trialing' | 'past_due'

export interface AISubscription {
    id: number
    status: AISubscriptionStatus
    cancelAtPeriodEnd: boolean
    currentPeriodStart: string | null
    currentPeriodEnd: string | null
    periodAllowance: number
    plan: AIPlan | null
    /** Tokens left of this period and what they cost; an upgrade takes it off the price. */
    unused?: { tokens: number; value: number }
    /** A downgrade: `plan` stays until `effectiveFrom` (the next renewal), then this plan starts. */
    scheduledPlan?: {
        id: number
        name: string | null
        price: number
        currency: string
        tokenAmount: number
        billingInterval: BillingInterval | null
        effectiveFrom: string | null
    }
    /** New terms for this plan that start at `from` (the next renewal). */
    scheduledChange?: {
        from: string | null
        price: number
        currency: string
        tokenAmount: number
        bonusTokens?: number
        billingInterval: BillingInterval | null
    }
}

export type AIPurchaseStatus = 'pending' | 'paid' | 'credited' | 'failed' | 'expired'

export interface AIPurchaseRow {
    id: number
    packageName: string
    kind: 'topup' | 'subscription'
    tokenAmount: number
    bonusTokens: number
    amount: number | string
    currency: string
    status: AIPurchaseStatus
    checkoutSessionId: string | null
    creditedAt: string | null
    createdAt: string
}

export interface CheckoutResponse {
    purchaseId: number
    checkoutSessionId: string
    url: string
}

// ─── Endpoints ───────────────────────────────────────────────────────────────

export const getAIOverview = async (): Promise<AIOverview> => {
    const res = await api.get('/ai/overview')
    return res.data
}

export const getAIUsage = async (page = 1, length = 10): Promise<{ usage: AIUsageRow[]; pagination: AIPagination }> => {
    const res = await api.get('/ai/usage', { params: { page, length } })
    return res.data
}

export const getAITransactions = async (page = 1, length = 10): Promise<{ balance: AIBalance | null; transactions: AITransactionRow[]; pagination: AIPagination }> => {
    const res = await api.get('/ai/transactions', { params: { page, length } })
    return res.data
}

export const getAIGenerations = async (params: { listingId?: number; page?: number; length?: number } = {}): Promise<{ generations: AIGenerationRow[]; pagination: AIPagination }> => {
    const res = await api.get('/ai/generations', { params })
    return res.data
}

export const acceptAIGeneration = async (generationId: number): Promise<{ status: true }> => {
    const res = await api.post(`/ai/generations/${generationId}/accept`, {}, JSON_HEADERS)
    return res.data
}

export const generateListingCopy = async (body: { listingId?: number; draft: ListingDraft; notes: ListingNotes }): Promise<GenerateListingResponse> => {
    const res = await api.post('/ai/listings/generate', body, { ...JSON_HEADERS, timeout: 90000 })
    return afterRun(res.data)
}

export const enhanceListingCopy = async (listingId: number, body: { action: EnhanceActionKey; text?: string; language?: ListingLanguage; notes?: Record<string, unknown> }): Promise<EnhanceListingResponse> => {
    const res = await api.post(`/ai/listings/${listingId}/enhance`, body, { ...JSON_HEADERS, timeout: 90000 })
    return afterRun(res.data)
}

export const generateSocialPosts = async (listingId: number, body: { platforms?: SocialPlatform[]; notes?: Record<string, unknown> } = {}): Promise<SocialResponse> => {
    const res = await api.post(`/ai/listings/${listingId}/social`, body, { ...JSON_HEADERS, timeout: 90000 })
    return afterRun(res.data)
}

export const generateVideoBrief = async (listingId: number, body: { durationSeconds?: number; aspectRatio?: VideoAspectRatio; notes?: Record<string, unknown> } = {}): Promise<VideoBriefResponse> => {
    const res = await api.post(`/ai/listings/${listingId}/video-brief`, body, { ...JSON_HEADERS, timeout: 90000 })
    return afterRun(res.data)
}

export const getAIPackages = async (): Promise<{ packages: AIPackage[]; plans: AIPlan[]; subscription: AISubscription | null; purchases: { enabled: boolean } }> => {
    const res = await api.get('/ai/packages')
    return res.data
}

export const getAISubscription = async (): Promise<{ subscription: AISubscription | null }> => {
    const res = await api.get('/ai/subscription')
    return res.data
}

/**
 * Checkout for a first plan (`url`), or the plan changed in place (`changed`).
 * An upgrade the bank wants to confirm answers `requiresAction` with the
 * Stripe page to finish it on (`url`); `resumed` says a plan that was set to
 * cancel goes on again.
 */
export const subscribeToPlan = async (
    packageId: number,
): Promise<{
    purchaseId?: number
    checkoutSessionId?: string
    changed?: boolean
    upgrade?: boolean
    requiresAction?: boolean
    resumed?: boolean
    /** Choosing the current plan again called off a scheduled downgrade. */
    kept?: boolean
    /** Checkout for a first plan; with `requiresAction`, the Stripe page to finish the payment on, when there is one. */
    url?: string | null
    subscription?: AISubscription | null
}> => {
    const res = await api.post(`/ai/plans/${packageId}/subscribe`, {}, JSON_HEADERS)
    return res.data
}

/** Cancels at the end of the period, or undoes that with `resume`. */
export const cancelAISubscription = async (resume = false): Promise<{ subscription: AISubscription | null }> => {
    const res = await api.post(`/ai/subscription/${resume ? 'resume' : 'cancel'}`, {}, JSON_HEADERS)
    return res.data
}

export const openBillingPortal = async (): Promise<{ url: string }> => {
    const res = await api.post('/ai/billing-portal', {}, JSON_HEADERS)
    return res.data
}

export const checkoutAIPackage = async (packageId: number): Promise<CheckoutResponse> => {
    const res = await api.post(`/ai/packages/${packageId}/checkout`, {}, JSON_HEADERS)
    return res.data
}

/** Where a checkout that has just been opened is remembered, for this tab. */
export const PENDING_PURCHASE_KEY = 'hero:pending-purchase'

/** Closes a checkout that was left without paying. The API asks Stripe first. */
export const cancelAIPurchase = async (purchaseId: number | string): Promise<void> => {
    await api.post(`/ai/purchases/${purchaseId}/cancel`, {}, JSON_HEADERS)
}

export const getAIPurchases = async (page = 1, length = 10): Promise<{ purchases: AIPurchaseRow[]; pagination: AIPagination }> => {
    const res = await api.get('/ai/purchases', { params: { page, length } })
    return res.data
}

export const getAIPurchaseStatus = async (sessionId: string): Promise<{ purchase: AIPurchaseRow | null; balance: AIBalance | null }> => {
    const res = await api.get('/ai/purchases/status', { params: { session_id: sessionId } })
    return res.data
}

// ─── Errors ──────────────────────────────────────────────────────────────────

export const aiErrorBody = (err: unknown): AIErrorBody | null => {
    const data = (err as { response?: { data?: unknown } })?.response?.data
    if (data && typeof data === 'object' && 'error' in data) return data as AIErrorBody
    return null
}

export const aiErrorCode = (err: unknown): AIErrorCode | null => aiErrorBody(err)?.code ?? null

export const isInsufficientTokens = (err: unknown): boolean => aiErrorCode(err) === 'AI_INSUFFICIENT_TOKENS'

/** Said when a run is refused for want of tokens and the API gave no words of its own. */
export const OUT_OF_TOKENS_MESSAGE = 'Not enough AI tokens. Top up to continue.'

/** Human-readable message for any AI request failure. */
export const aiErrorMessage = (err: unknown): string => {
    const body = aiErrorBody(err)
    if (body?.code === 'AI_INSUFFICIENT_TOKENS') {
        // The API's own message is already in tokens; the figures are a fallback.
        if (body.error) return body.error
        const required = body.details?.requiredTokens ?? body.details?.required
        const available = body.details?.availableTokens ?? body.details?.available
        if (typeof required === 'number' && typeof available === 'number') {
            return `Not enough AI tokens. This needs about ${formatTokens(required)} tokens and you have ${formatTokens(available)}. Top up to continue.`
        }
        return OUT_OF_TOKENS_MESSAGE
    }
    if (body?.error) return body.error
    return Helper.errorHandler(err)
}

/** Formats a token count with thousands separators. */
export const formatTokens = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '0'
    return Number(value).toLocaleString('en-MY')
}

/** Formats a date as `21 Oct 2026`. */
export const formatDate = (value: string | null | undefined): string => {
    if (!value) return '—'
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Formats a money amount as `RM 12.00` (or the given currency code). */
export const formatMoney = (amount: number | string | null | undefined, currency = 'MYR'): string => {
    const value = Number(amount ?? 0)
    const code = (currency || 'MYR').toUpperCase()
    const prefix = code === 'MYR' ? 'RM' : code
    return `${prefix} ${Number.isFinite(value) ? value.toFixed(2) : '0.00'}`
}
